import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const TURNSTILE_CALLBACK = '__onloadTurnstileCallback';
const TURNSTILE_SCRIPT_BASE = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const TURNSTILE_SCRIPT_SRC = `${TURNSTILE_SCRIPT_BASE}?onload=${TURNSTILE_CALLBACK}&render=explicit`;

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement | string, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
    };
    ReactNativeWebView?: { postMessage: (data: string) => void };
    [TURNSTILE_CALLBACK]?: () => void;
  }
}

type Status =
  | { kind: 'loading' }
  | { kind: 'success'; token: string }
  | { kind: 'expire' }
  | { kind: 'error'; error: string };

function post(payload: unknown) {
  const data = JSON.stringify(payload);
  // eslint-disable-next-line no-console
  console.log('[challenge]', payload);
  if (window.ReactNativeWebView?.postMessage) {
    window.ReactNativeWebView.postMessage(data);
  } else if (window.parent !== window) {
    window.parent.postMessage(data, '*');
  }
}

export default function ChallengePage() {
  const { search } = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  const params = new URLSearchParams(search);
  const siteKey = params.get('siteKey') ?? '';
  const action = params.get('action') ?? '';
  const theme = (params.get('theme') ?? 'light') as 'light' | 'dark' | 'auto';

  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    if (!siteKey) {
      const err = 'missing-sitekey';
      setStatus({ kind: 'error', error: err });
      post({ type: 'error', error: err });
      return;
    }

    const renderWidget = () => {
      if (!containerRef.current || !window.turnstile || widgetIdRef.current) {
        return;
      }
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: action || undefined,
          theme,
          retry: 'auto',
          'retry-interval': 8000,
          'refresh-expired': 'auto',
          appearance: 'always',
          callback: (token: string) => {
            setStatus({ kind: 'success', token });
            post({ type: 'success', token });
          },
          'error-callback': (err: unknown) => {
            const e = String(err);
            setStatus({ kind: 'error', error: e });
            post({ type: 'error', error: e });
          },
          'expired-callback': () => {
            setStatus({ kind: 'expire' });
            post({ type: 'expire' });
          },
        });
      } catch (e) {
        const err = `render-failed: ${e instanceof Error ? e.message : String(e)}`;
        setStatus({ kind: 'error', error: err });
        post({ type: 'error', error: err });
      }
    };

    window[TURNSTILE_CALLBACK] = renderWidget;

    if (window.turnstile) {
      renderWidget();
    } else if (!document.querySelector(`script[src^="${TURNSTILE_SCRIPT_BASE}"]`)) {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        const err = 'script-load-failed';
        setStatus({ kind: 'error', error: err });
        post({ type: 'error', error: err });
      };
      document.head.appendChild(script);
    }
  }, [siteKey, action, theme]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        background: 'transparent',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div ref={containerRef} />
      <StatusBadge status={status} />
    </div>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const isWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;
  if (isWebView) return null;

  const base: React.CSSProperties = {
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: 13,
    maxWidth: 360,
    textAlign: 'center',
    wordBreak: 'break-all',
  };

  if (status.kind === 'loading') {
    return <div style={{ ...base, background: '#eef', color: '#446' }}>Aguardando challenge…</div>;
  }
  if (status.kind === 'expire') {
    return <div style={{ ...base, background: '#fef3c7', color: '#92400e' }}>Token expirado.</div>;
  }
  if (status.kind === 'error') {
    return (
      <div style={{ ...base, background: '#fee2e2', color: '#991b1b' }}>
        Erro: {status.error}
      </div>
    );
  }
  return (
    <div style={{ ...base, background: '#dcfce7', color: '#166534' }}>
      ✓ Token recebido ({status.token.slice(0, 20)}…)
    </div>
  );
}
